/**
 * Command Center tab copy — what each section enables and why it matters.
 */
import type { DashboardTab } from './DashboardPanel';

export type DashboardTabMeta = {
  id: DashboardTab;
  label: string;
  shortLabel: string;
  icon: string;
  tagline: string;
  enables: string;
  value: string;
  actions: string[];
};

export const DASHBOARD_TABS: DashboardTabMeta[] = [
  {
    id: 'checklist',
    label: 'Overview',
    shortLabel: 'Overview',
    icon: '📋',
    tagline: 'Mission control for your operation',
    enables: 'Building checklist, milestone progress, and audit export',
    value: 'See what you have vs. what regulators expect — know your next build before you waste budget.',
    actions: ['Track governance & security coverage', 'Export audit report', 'Review operational objectives'],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    shortLabel: 'Compliance',
    icon: '⚖️',
    tagline: 'Regulatory health & policy coverage',
    enables: 'Compliance score breakdown, building coverage, and violation risks',
    value: 'Keep compliance above failure thresholds — unlocks products, investors, and scenario wins.',
    actions: ['View compliance % and trends', 'See governance building gaps', 'Identify zone violations'],
  },
  {
    id: 'security',
    label: 'Security',
    shortLabel: 'Security',
    icon: '🛡️',
    tagline: 'Threat posture & defensive stack',
    enables: 'Threat level, security tooling coverage, and attack-surface analysis',
    value: 'Lower threat to prevent breaches, SOC alerts, and runaway incident costs.',
    actions: ['Monitor threat %', 'Audit cyber & security buildings', 'Prioritize defensive gaps'],
  },
  {
    id: 'research',
    label: 'All Research',
    shortLabel: 'Research',
    icon: '🧬',
    tagline: 'R&D lab & tech unlocks',
    enables: 'Start research projects, unlock buildings, and advance tier progression',
    value: 'Unlock SIEM, Zero Trust, and upgrades — multiplies compliance gains and late-game revenue.',
    actions: ['Start & track R&D projects', 'Unlock new building types', 'Advance tier 1 → 3 tech tree'],
  },
  {
    id: 'products',
    label: 'Products',
    shortLabel: 'Products',
    icon: '📦',
    tagline: 'Revenue products & launches',
    enables: 'Launch financial products when compliance gates are met',
    value: 'Adds passive $/tick income on top of building revenue — compounds over time.',
    actions: ['Check launch requirements', 'Launch products', 'View active product revenue'],
  },
  {
    id: 'finance',
    label: 'Finance',
    shortLabel: 'Finance',
    icon: '💰',
    tagline: 'Capital, debt & investor relations',
    enables: 'Loans, investor pitches, and cash-flow breakdown',
    value: 'Bridge budget gaps without stopping construction — manage interest before it drains you.',
    actions: ['Take or repay loans', 'Pitch investors (Jump Gate required)', 'View revenue vs. costs per tick'],
  },
  {
    id: 'orgmanagement',
    label: 'Org Mgmt',
    shortLabel: 'Org',
    icon: '👥',
    tagline: 'People, vendors & audit operations',
    enables: 'HR, TPRM, and Audit dashboards — workforce and third-party risk',
    value: 'Org simulation affects tick outcomes — proper staffing reduces incidents and fines.',
    actions: ['Open HR dashboard', 'Manage vendor risk (TPRM)', 'Run compliance audits'],
  },
];

export function getDashboardTabMeta(tab: DashboardTab): DashboardTabMeta {
  return DASHBOARD_TABS.find((t) => t.id === tab) ?? DASHBOARD_TABS[0];
}